import { MenuItem, Stack, useMediaQuery, useTheme } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { Organisation } from '@/domain/organisation/entities/organisation.entity';
import { selectOrganisationInfos } from '@/domain/organisation/organisation.selectors';
import { FSImageField } from '@/ui/components/input/FSImageField';
import { UploadImageServiceS3 } from '@/ui/components/input/FSImageField.utils/upload-image-service-s3';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { getAllCountryNames } from '@/ui/utils/country-names';
import { isNumber, isWebsiteUrl } from '@/ui/utils/form-validations';

interface IProps {
  organisation: Organisation;
  onSubmit: (data: Organisation) => Promise<void> | void;
  buttons?: React.ReactNode;
}

const countryNames = prepareCountryValues();

export function OrganisationForm({ organisation, onSubmit, buttons }: IProps) {
  const organisationId = useAppSelector(selectOrganisationInfos).id.toString();
  const imageDestinationFolder = organisationId;

  const theme = useTheme();
  const breakpointUpThanMd = useMediaQuery(theme.breakpoints.up('md'));
  const { handleSubmit, register, formState, control } = useForm<Organisation>({
    defaultValues: organisation,
  });
  const { errors } = formState;

  const handleFormSubmit = (data: Organisation) => {
    onSubmit({ ...data });
  };

  return (
    <form
      style={{ marginTop: 2, width: '100%' }}
      onSubmit={handleSubmit(handleFormSubmit)}
      noValidate
    >
      <Controller
        name="logo"
        control={control}
        render={({ field }) => (
          <FSImageField
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            uploadImageService={
              new UploadImageServiceS3(imageDestinationFolder)
            }
          />
        )}
      />

      <FSTextField
        label={translate('CreateOrganisation.Form.organisation-name')}
        defaultValue={organisation.name}
        required
        register={{
          ...register('name', {
            required: translate('Form.fill-the-field'),
          }),
        }}
        error={!!errors.name}
        helperText={errors.name?.message}
      />

      <FSTextField
        select
        label={translate('CreateOrganisation.Form.organisation-type')}
        defaultValue={organisation.type}
        required
        register={{
          ...register('type', {
            required: translate('Form.fill-the-field'),
          }),
        }}
        error={!!errors.type}
        helperText={errors.type?.message}
      >
        <MenuItem value="ASSOCIATION">
          {translate('CreateOrganisation.association')}
        </MenuItem>
        <MenuItem value="ENTERPRISE">
          {translate('CreateOrganisation.company')}
        </MenuItem>
        <MenuItem value="LOCAL_AUTHORITY">
          {translate('CreateOrganisation.local-authority')}
        </MenuItem>
        <MenuItem value="INDIVIDUAL">
          {translate('CreateOrganisation.individual')}
        </MenuItem>
        <MenuItem value="OTHER">
          {translate('CreateOrganisation.other')}
        </MenuItem>
      </FSTextField>

      <FSTextField
        label={translate('CreateOrganisation.Form.website')}
        defaultValue={organisation.website}
        register={{
          ...register('website', {
            validate: (value) => (value ? isWebsiteUrl(value) : true),
          }),
        }}
        error={!!errors.website}
        helperText={errors.website?.message}
      />

      <FSTextField
        select
        label={translate('CreateOrganisation.Form.activity')}
        defaultValue={organisation.activity}
        register={{
          ...register('activity'),
        }}
        error={!!errors.activity}
        helperText={errors.activity?.message}
      >
        <MenuItem value="FESTIVAL">
          {translate('CreateOrganisation.Form.activity-values.festival')}
        </MenuItem>
        <MenuItem value="TOUR">
          {translate('CreateOrganisation.Form.activity-values.tour')}
        </MenuItem>
        <MenuItem value="VENUE">
          {translate('CreateOrganisation.Form.activity-values.venue')}
        </MenuItem>
      </FSTextField>

      <FSTextField
        label={translate('CreateOrganisation.Form.address')}
        defaultValue={organisation.address}
        register={{
          ...register('address'),
        }}
        error={!!errors.address}
        helperText={errors.address?.message}
      />

      <Stack
        display={'flex'}
        direction={breakpointUpThanMd ? 'row' : 'column'}
        sx={{ width: '100%' }}
      >
        <FSTextField
          sx={{ width: '50%', mr: 1 }}
          label={translate('CreateOrganisation.Form.zip')}
          defaultValue={organisation.zipCode}
          maxLength={5}
          register={{
            ...register('zipCode', {
              validate: isNumber,
            }),
          }}
          error={!!errors.zipCode}
          helperText={errors.zipCode?.message}
        />
        <FSTextField
          label={translate('CreateOrganisation.Form.location')}
          defaultValue={organisation.location}
          required
          register={{
            ...register('location', {
              required: translate('Form.fill-the-field'),
            }),
          }}
          error={!!errors.location}
          helperText={errors.location?.message}
        />
      </Stack>

      <FSTextField
        select
        label={translate('CreateOrganisation.Form.country')}
        defaultValue={organisation.country}
        required
        register={{
          ...register('country', {
            required: translate('Form.fill-the-field'),
          }),
        }}
        error={!!errors.country}
        helperText={errors.country?.message}
      >
        {countryNames.map(({ code, name }) => (
          <MenuItem key={code} value={code}>
            {name}
          </MenuItem>
        ))}
      </FSTextField>

      {buttons && buttons}
    </form>
  );
}

function prepareCountryValues() {
  return Object.entries(getAllCountryNames()).map(([key, value]) => ({
    code: key,
    name: value,
  }));
}
